/**
 * Stretched Cluster selectors.
 *
 * All selectors derive from DataKeys.HostAggregates — no separate data key.
 * They filter by pf9_failure_domain metadata, compute health status from
 * host connectivity, and provide lookup maps used across Parts B–H.
 *
 * Exports:
 * - stretchedClustersSelector: filtered + enriched list of all stretched clusters
 * - stretchedClusterPairsSelector: grouped by cluster into {domainA, domainB} pairs
 * - stretchedClusterByHostIdSelector: host ID → domain lookup (for host list columns)
 * - stretchedClustersByClusterSelector: cluster name → domain pair lookup (for validation/columns)
 */

import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import { resmgrHostsByIdSelector } from 'openstack/components/infrastructure/selectors'
import {
  STRETCHED_CLUSTER_LABEL_KEY,
  STRETCHED_CLUSTER_METADATA_KEY,
  STRETCHED_CLUSTER_METADATA_VALUE,
  StretchedClusterLabel,
  StretchedClusterStatus,
} from './constants'
import { StretchedCluster, StretchedClusterPair } from './models'

const getHostStatus = (hostId: string, resmgrHostsById: Record<string, any>) => {
  const host = resmgrHostsById[hostId]
  return host?.responding === 'online'
}

const getDomainStatus = (
  hosts: string[],
  resmgrHostsById: Record<string, any>,
): { status: StretchedClusterStatus; onlineCount: number } => {
  const onlineCount = hosts.filter((hostId) => getHostStatus(hostId, resmgrHostsById)).length
  const totalCount = hosts.length
  if (totalCount > 0 && onlineCount === 0) {
    return { status: StretchedClusterStatus.Offline, onlineCount }
  }
  if (onlineCount < totalCount) {
    return { status: StretchedClusterStatus.Warning, onlineCount }
  }
  return { status: StretchedClusterStatus.Healthy, onlineCount }
}

/**
 * Filters host aggregates to only stretched clusters, enriched with status info.
 */
export const stretchedClustersSelector = createSharedSelector(
  getDataSelector<DataKeys.HostAggregates>(DataKeys.HostAggregates),
  resmgrHostsByIdSelector,
  (aggregates, resmgrHostsById): StretchedCluster[] => {
    return aggregates
      .filter(
        (agg) =>
          agg?.metadata?.[STRETCHED_CLUSTER_METADATA_KEY] === STRETCHED_CLUSTER_METADATA_VALUE,
      )
      .map((agg) => {
        const { status, onlineCount } = getDomainStatus(agg.hosts || [], resmgrHostsById)
        return {
          id: agg.id,
          name: agg.name,
          label: agg.metadata?.[STRETCHED_CLUSTER_LABEL_KEY] as StretchedClusterLabel,
          clusterName: agg.availability_zone || '',
          hosts: agg.hosts || [],
          metadata: agg.metadata || {},
          status,
          onlineHostCount: onlineCount,
          totalHostCount: (agg.hosts || []).length,
        }
      })
  },
)

/**
 * Groups stretched clusters by cluster into pairs { clusterName, domainA, domainB }.
 */
export const stretchedClusterPairsSelector = createSharedSelector(
  stretchedClustersSelector,
  (domains): StretchedClusterPair[] => {
    const byCluster = new Map<string, { a: StretchedCluster | null; b: StretchedCluster | null }>()
    for (const domain of domains) {
      const entry = byCluster.get(domain.clusterName) || { a: null, b: null }
      if (domain.label === StretchedClusterLabel.A) {
        entry.a = domain
      } else if (domain.label === StretchedClusterLabel.B) {
        entry.b = domain
      }
      byCluster.set(domain.clusterName, entry)
    }
    return Array.from(byCluster.entries()).map(([clusterName, { a, b }]) => ({
      clusterName,
      domainA: a,
      domainB: b,
    }))
  },
)

/**
 * Maps host ID → stretched cluster (name + id). Used in host list columns.
 */
export const stretchedClusterByHostIdSelector = createSharedSelector(
  stretchedClustersSelector,
  (domains): Record<string, { id: string; name: string; label: StretchedClusterLabel }> => {
    const map: Record<string, { id: string; name: string; label: StretchedClusterLabel }> = {}
    for (const domain of domains) {
      for (const hostId of domain.hosts) {
        map[hostId] = { id: domain.id, name: domain.name, label: domain.label }
      }
    }
    return map
  },
)

/**
 * Maps cluster name → stretched cluster pair. Used in Parts F, G, H.
 */
export const stretchedClustersByClusterSelector = createSharedSelector(
  stretchedClusterPairsSelector,
  (pairs): Record<string, StretchedClusterPair> => {
    const map: Record<string, StretchedClusterPair> = {}
    for (const pair of pairs) {
      map[pair.clusterName] = pair
    }
    return map
  },
)

/**
 * Maps stretched cluster aggregate UUID → domain name.
 * Used by VM selectors to resolve metadata['pf9_failure_domain'] to a display name.
 */
export const stretchedClusterNameByIdSelector = createSharedSelector(
  stretchedClustersSelector,
  (domains): Record<string, string> => {
    const map: Record<string, string> = {}
    for (const domain of domains) {
      map[String(domain.id)] = domain.name
    }
    return map
  },
)

/**
 * Returns a Set of cluster names where at least one hypervisor host
 * is in a stretched-cluster-enabled cluster but has no FDM assigned.
 * Used to show warning indicators on the cluster list page.
 */
export const clustersWithUnassignedFdmHostsSelector = createSharedSelector(
  stretchedClusterPairsSelector,
  resmgrHostsByIdSelector,
  (pairs, hostsById): Set<string> => {
    const result = new Set<string>()
    for (const pair of pairs) {
      const assignedHostIds = new Set([
        ...(pair.domainA?.hosts ?? []),
        ...(pair.domainB?.hosts ?? []),
      ])
      // resmgrHostsByIdSelector returns Record<string, {}> (untyped host objects from hosts.reduce())
      const clusterHosts = Object.values(hostsById).filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (h: any) => h.clusterName === pair.clusterName && h.roles?.includes('hypervisor'),
      )
      if (clusterHosts.some((h: any) => !assignedHostIds.has(h.id))) {
        // any: same untyped source
        result.add(pair.clusterName)
      }
    }
    return result
  },
)
